import { Localizable } from '@components/localizable/Localizable'
import { AvailableLocale } from '@models/Lang'
import {
  DemoTabLocaleTexts,
  DemoTabTextsMap,
} from '@services/MembershipDemoTabTextsService'
import { Alert, Button, Card, Flex, Form, Space, Typography } from 'antd'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { DEFAULT_ERROR } from '../App'
import { langList } from '../factories/Utils'

const { Title, Text } = Typography

const TITLE_MAX_LENGTH = 120
const DESCRIPTION_MAX_LENGTH = 1000

const FIELDS = ['title', 'description'] as const
type Field = (typeof FIELDS)[number]

/** An empty field falls back to the app's built-in value, which these name. */
const TITLE_PLACEHOLDER = 'Default title'
const DESCRIPTION_PLACEHOLDER = 'Default intro text'

interface Props {
  title: ReactNode
  /** Helper text rendered inside the card, under the inputs. */
  description?: ReactNode
  /**
   * The texts currently stored. `undefined` until they are loaded: the editor
   * stays disabled meanwhile, since saving replaces the whole map and would
   * otherwise wipe texts it never showed.
   */
  current: DemoTabTextsMap | null | undefined
  /** Why the stored texts could not be loaded, if they could not. */
  loadError?: string
  /** Save every locale's texts. A rejection keeps the typed values in place. */
  onSubmit: (
    texts: Record<AvailableLocale, DemoTabLocaleTexts>
  ) => Promise<unknown>
}

interface SaveRejection {
  message?: string
  error?: {
    response?: {
      data?: { message?: string; errors?: Record<string, string[]> }
    }
  }
}

type FormValues = Partial<
  Record<AvailableLocale, Partial<Record<Field, string | null>>>
>

const toNullable = (value?: string | null) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/**
 * Title and intro text of the membership demo tab, through the localizable
 * fields used across the settings. Save always sends every locale and both
 * fields, since the API replaces the whole map; an empty field is sent as `null`
 * so the app falls back to its own text.
 */
const DemoTabTextsEditor = ({
  title,
  description,
  current,
  loadError,
  onSubmit,
}: Props) => {
  const [form] = Form.useForm<FormValues>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>()
  const isLoaded = current !== undefined

  /**
   * The fields have no rules, so the form never re-validates them: an API error
   * set on one would otherwise stay after the value is fixed.
   */
  const clearFieldErrors = (names: [AvailableLocale, Field][]) =>
    form.setFields(names.map((name) => ({ name, errors: [] })))

  // The localizable fields write through `setFieldValue`, which fires no
  // `onValuesChange`: watch the values to clear the error of an edited field.
  const values = Form.useWatch([], form)
  const previousValues = useRef<FormValues>(undefined)
  useEffect(() => {
    const previous = previousValues.current
    previousValues.current = values
    if (!previous || !values) return
    clearFieldErrors(
      langList.flatMap((lang) =>
        FIELDS.filter(
          (field) => previous[lang.code]?.[field] !== values[lang.code]?.[field]
        ).map((field): [AvailableLocale, Field] => [lang.code, field])
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  useEffect(() => {
    if (!isLoaded) return
    form.setFieldsValue(
      Object.fromEntries(
        langList.map((lang) => [
          lang.code,
          {
            title: current?.[lang.code]?.title ?? '',
            description: current?.[lang.code]?.description ?? '',
          },
        ])
      )
    )
  }, [current, form, isLoaded])

  /**
   * The API answers a refused value with Laravel's bare validation payload,
   * which SBAPI does not unwrap: map `texts.<locale>.<field>` errors onto the
   * matching inputs, and show anything else above them.
   */
  const showRejection = (rejection?: SaveRejection) => {
    const data = rejection?.error?.response?.data
    const fieldErrors = langList.flatMap((lang) =>
      FIELDS.flatMap((field) => {
        const errors = data?.errors?.[`texts.${lang.code}.${field}`]
        return errors?.length
          ? [{ name: [lang.code, field] as [AvailableLocale, Field], errors }]
          : []
      })
    )

    if (fieldErrors.length) {
      form.setFields(fieldErrors)
      return
    }
    setError(data?.message ?? rejection?.message ?? DEFAULT_ERROR)
  }

  const onFinish = (values: FormValues) => {
    if (!isLoaded) return

    const texts = Object.fromEntries(
      langList.map((lang) => [
        lang.code,
        {
          title: toNullable(values[lang.code]?.title),
          description: toNullable(values[lang.code]?.description),
        },
      ])
    ) as Record<AvailableLocale, DemoTabLocaleTexts>

    clearFieldErrors(
      langList.flatMap((lang) =>
        FIELDS.map((field): [AvailableLocale, Field] => [lang.code, field])
      )
    )
    setIsLoading(true)
    setError(undefined)
    onSubmit(texts)
      .catch(showRejection)
      .finally(() => setIsLoading(false))
  }

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      disabled={!isLoaded}
      onFinish={onFinish}
    >
      <Flex vertical className="w-full" gap={8}>
        <Space className="w-full space-between">
          <Title level={5} className="m-0">
            {title}
          </Title>
          <Button
            style={{ border: 0 }}
            type="primary"
            loading={isLoading}
            htmlType="submit"
          >
            Save
          </Button>
        </Space>

        <Card>
          <Flex vertical>
            {loadError && (
              <Alert
                type="error"
                showIcon
                title={loadError}
                style={{ marginBottom: 16 }}
              />
            )}
            {error && (
              <Alert
                type="error"
                showIcon
                title={error}
                style={{ marginBottom: 16 }}
              />
            )}
            <Localizable.Input
              label="Title"
              name="title"
              max={TITLE_MAX_LENGTH}
              placeholder={TITLE_PLACEHOLDER}
            />
            <Localizable.TextArea
              label="Text"
              name="description"
              max={DESCRIPTION_MAX_LENGTH}
              placeholder={DESCRIPTION_PLACEHOLDER}
            />
            {description && (
              <Card styles={{ body: { padding: 10 } }}>
                <Text type="secondary">{description}</Text>
              </Card>
            )}
          </Flex>
        </Card>
      </Flex>
    </Form>
  )
}

export default DemoTabTextsEditor
